/** Styles visuels pour reconstruire le modèle « La Table des Jardins » avec texte / image / bouton. */

import {
  applySectionGridRowsForViewport,
  resolveSectionConfiguredGridRows,
  viewportSectionGridRowsField,
} from './sectionGrid';
import { ensureJardinsResponsiveLayout } from './jardinsResponsive';

export const DESIGN_KITS = {
  jardins: {
    id: 'jardins',
    label: 'La Table des Jardins',
    sectionClass: 'wc-design-kit-jardins',
    /** Hauteur de ligne grille (px) — calée sur le rythme vertical du site exemple */
    gridRowHeight: 18,
  },
};

export function gridRowHeightForSection(section) {
  const kit = getSectionDesignKit(section);
  return kit?.gridRowHeight || null;
}

/** Lit la hauteur de ligne depuis la grille éditeur (`data-grid-row-height`). */
export function gridRowHeightFromElement(gridEl) {
  if (!gridEl?.dataset) return null;
  const n = Number(gridEl.dataset.gridRowHeight);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function kitUsesFixedRows(section) {
  return Boolean(getSectionDesignKit(section));
}

export function isJardinsForestSection(section) {
  return (
    section?.designKit === 'jardins' &&
    (section.id === 'sec-j-saison' || section.kitSectionTone === 'forest')
  );
}

/** Grille kit : lignes fixes si hauteur section imposée par l'utilisateur. */
export function kitGridTrackStyle(
  section,
  totalGridRows,
  kitRowH,
  viewport = 'desktop',
  options = {},
) {
  const { forceFixedRowHeight = false, heroViewportFill = false } = options;
  if (heroViewportFill && totalGridRows > 0) {
    return {
      gridTemplateRows: `repeat(${totalGridRows}, minmax(0, 1fr))`,
      gridAutoRows: 'minmax(0, 1fr)',
      minHeight: '100%',
      height: '100%',
    };
  }
  const forestFixed = isJardinsForestSection(section) && viewport === 'desktop';
  const userFixed =
    section?.gridRowsCustom === true &&
    resolveSectionConfiguredGridRows(section, viewport) > 0;
  const fixedTracks = forceFixedRowHeight || forestFixed || userFixed;
  const rowTrack = fixedTracks ? `${kitRowH}px` : `minmax(${kitRowH}px, auto)`;

  return {
    gridAutoRows: fixedTracks ? `${kitRowH}px` : `minmax(${kitRowH}px, auto)`,
    gridTemplateRows: `repeat(${totalGridRows}, ${rowTrack})`,
  };
}

/** Photo saison : occupe toute la hauteur de grille calculée (pas le rowSpan stocké). */
export function forestFillGridPlacement(placement, totalGridRows) {
  if (!placement || !totalGridRows) return placement;
  return {
    ...placement,
    row: 0,
    rowSpan: totalGridRows,
    colStart: placement.colStart || 1,
    colSpan: placement.colSpan || 24,
  };
}

/** Images calées sur la cellule ; texte libre (pas de maxHeight). */
export function kitGridItemSizeStyle(
  section,
  comp,
  rowSpan,
  kitRowH,
  { hubupType = false, viewport = 'desktop' } = {},
) {
  const h = rowSpan * kitRowH;
  const kit = getSectionDesignKit(section);
  if (!kit) {
    if (
      comp?.type === 'image' ||
      comp?.type === 'cardFeature' ||
      comp?.type === 'cardIllustration' ||
      comp?.type === 'cardSplit'
    ) {
      return {
        minHeight: h,
        maxHeight: h,
        height: h,
        overflow: 'hidden',
        alignSelf: 'start',
      };
    }
    if (comp?.type === 'text') {
      return {
        minHeight: h,
        maxHeight: h,
        overflow: 'hidden',
        alignSelf: 'start',
      };
    }
    if (
      (comp?.type === 'reservation' || comp?.type === 'clickAndCollect') &&
      comp?.props?.displayMode === 'inline'
    ) {
      return {
        minHeight: h,
        maxHeight: h,
        height: h,
        overflow: 'hidden',
        alignSelf: 'start',
      };
    }
    return {
      minHeight: h,
      ...(hubupType ? { maxHeight: h, overflow: 'hidden' } : {}),
    };
  }
  const isImage = comp?.type === 'image';
  const bookingCard =
    comp?.type === 'reservation' && comp?.props?.layoutMode === 'booking-card';
  if (
    isImage &&
    comp?.props?.kitStyle === 'jardins-fill' &&
    isJardinsForestSection(section) &&
    viewport === 'desktop'
  ) {
    return {
      minHeight: 0,
      height: '100%',
      maxHeight: 'none',
      alignSelf: 'stretch',
      overflow: 'hidden',
    };
  }
  if (isImage) {
    return {
      minHeight: h,
      maxHeight: h,
      height: h,
      overflow: 'hidden',
      alignSelf: 'start',
    };
  }
  if (comp?.type === 'text' && comp?.props?.kitStyle === 'jardins-announcement') {
    return {
      minHeight: h,
      height: h,
      maxHeight: h,
      width: '100%',
      alignSelf: 'stretch',
      overflow: 'hidden',
    };
  }
  if (bookingCard) {
    return {
      minHeight: h,
      maxHeight: h,
      height: h,
      overflow: 'hidden',
      alignSelf: 'stretch',
    };
  }
  const headerCell =
    section?.kitSectionTone === 'header' &&
    ((comp?.type === 'button' &&
      (comp?.props?.kitStyle === 'jardins-nav-link' ||
        comp?.props?.kitStyle === 'jardins-nav-cta')) ||
      (comp?.type === 'text' && comp?.props?.kitStyle === 'jardins-brand'));
  if (headerCell) {
    return {
      minHeight: h,
      height: h,
      alignSelf: 'stretch',
      overflow: 'visible',
    };
  }
  if (comp?.type === 'text' || comp?.type === 'button') {
    return {
      minHeight: h,
      height: 'auto',
      overflow: 'visible',
      alignSelf: 'start',
    };
  }
  return {
    minHeight: h,
    overflow: 'visible',
  };
}

/** Z-index grille kit (à fusionner avec layout composant). */
export function kitGridZIndex(comp, layoutZ = 1) {
  if (comp?.type === 'image' && comp?.props?.kitStyle === 'jardins-fill') {
    return 1;
  }
  if (comp?.type === 'image') {
    return Math.max(layoutZ, 2);
  }
  if (comp?.type === 'text' || comp?.type === 'button' || comp?.type === 'reservation') {
    return Math.max(layoutZ, 10);
  }
  return layoutZ;
}

export const TEXT_KIT_STYLES = [
  { id: '', label: 'Par défaut (éditeur)' },
  { id: 'jardins-announcement', label: 'Bandeau annonce' },
  { id: 'jardins-brand', label: 'Logo / marque' },
  { id: 'jardins-eyebrow', label: 'Surtitre' },
  { id: 'jardins-eyebrow-light', label: 'Surtitre clair' },
  { id: 'jardins-hero-title', label: 'Titre hero' },
  { id: 'jardins-hero-copy', label: 'Accroche hero' },
  { id: 'jardins-h2', label: 'Titre de section' },
  { id: 'jardins-seasonal-h2', label: 'Titre saison (grand)' },
  { id: 'jardins-reservation-title', label: 'Titre réservation (grand)' },
  { id: 'jardins-reservation-note', label: 'Encart réservation' },
  { id: 'jardins-quote-mark', label: 'Guillemet citation' },
  { id: 'jardins-quote-flower', label: 'Fleur décor citation' },
  { id: 'jardins-info-block', label: 'Bloc infos pratiques' },
  { id: 'jardins-body', label: 'Corps de texte' },
  { id: 'jardins-seasonal-tags', label: 'Tags saison (puces)' },
  { id: 'jardins-heading-note', label: 'Note (colonne droite)' },
  { id: 'jardins-underline-link', label: 'Lien souligné' },
  { id: 'jardins-photo-caption', label: 'Légende photo' },
  { id: 'jardins-dish-meta', label: 'Meta plat (petit)' },
  { id: 'jardins-dish-title', label: 'Titre plat' },
  { id: 'jardins-dish-desc', label: 'Description plat' },
  { id: 'jardins-disclaimer', label: 'Mention légale' },
  { id: 'jardins-quote', label: 'Citation' },
  { id: 'jardins-quote-by', label: 'Signature citation' },
  { id: 'jardins-footer-tagline', label: 'Phrase pied de page' },
  { id: 'jardins-footer-small', label: 'Pied de page (petit)' },
  { id: 'jardins-hero-side', label: 'Note verticale hero' },
  { id: 'jardins-hero-bottom', label: 'Ligne bas hero' },
  { id: 'jardins-hero-bottom-left', label: 'Hero bas (gauche)' },
  { id: 'jardins-hero-bottom-right', label: 'Hero bas (droite)' },
  { id: 'jardins-stamp', label: 'Tampon circulaire' },
  { id: 'jardins-seasonal-number', label: 'Numéro décoratif' },
];

export const BUTTON_KIT_STYLES = [
  { id: '', label: 'Style bouton classique' },
  { id: 'jardins-btn-cream', label: 'Jardins — crème' },
  { id: 'jardins-btn-outline', label: 'Jardins — contour' },
  { id: 'jardins-btn-forest', label: 'Jardins — vert forêt' },
  { id: 'jardins-text-link-light', label: 'Jardins — lien texte clair' },
  { id: 'jardins-nav-link', label: 'Jardins — lien navigation' },
  { id: 'jardins-nav-cta', label: 'Jardins — CTA navigation' },
];

export const IMAGE_KIT_STYLES = [
  { id: '', label: 'Image standard' },
  { id: 'jardins-fill', label: 'Remplir la cellule (cover)' },
  { id: 'jardins-dish', label: 'Photo plat' },
  { id: 'jardins-intro-photo', label: 'Photo intro (390px)' },
  { id: 'jardins-gallery', label: 'Photo galerie' },
];

export function getSectionDesignKit(section) {
  const kit = section?.designKit;
  if (!kit || !DESIGN_KITS[kit]) return null;
  return DESIGN_KITS[kit];
}

export function siteUsesDesignKit(site, kitId = 'jardins') {
  return (site?.sections || []).some((sec) => sec.designKit === kitId);
}

const ALIGN_MAP = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
};

export function kitCellAlignStyle(props, compType = '') {
  if (!props?.kitStyle && !props?.kitVAlign && !props?.kitHAlign) return null;
  const v = props.kitVAlign || 'start';
  const h = props.kitHAlign || 'stretch';
  const navBtn =
    compType === 'button' &&
    (props.kitStyle === 'jardins-nav-link' || props.kitStyle === 'jardins-nav-cta');
  const headerAlign =
    navBtn || (compType === 'text' && props.kitStyle === 'jardins-brand');
  if (headerAlign) {
    return {
      display: 'flex',
      flexDirection: 'column',
      alignItems: h === 'stretch' ? 'stretch' : ALIGN_MAP[h] || 'flex-start',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      minHeight: 0,
      flex: '1 1 auto',
      boxSizing: 'border-box',
    };
  }
  const fillHeight =
    compType === 'image' ||
    props.kitStyle === 'jardins-announcement' ||
    props.kitStyle === 'jardins-fill';
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: h === 'stretch' ? 'stretch' : ALIGN_MAP[h] || 'flex-start',
    justifyContent: ALIGN_MAP[v] || 'flex-start',
    width: '100%',
    height: fillHeight ? '100%' : 'auto',
    minHeight: fillHeight ? 0 : undefined,
    flex: fillHeight ? '1 1 auto' : '0 0 auto',
    boxSizing: 'border-box',
  };
}

export function siteHasMonolithRestaurant(site) {
  for (const sec of site?.sections || []) {
    for (const c of sec.components || []) {
      if (c.type === 'restaurantPage' || c.type === 'restaurantSection') return true;
    }
  }
  return false;
}

/** Monolithe restaurant legacy — pas les pages kit Jardins (composants atomiques). */
export function shouldRenderMonolithRestaurant(site) {
  return siteHasMonolithRestaurant(site) && !siteUsesDesignKit(site, 'jardins');
}

/** Tons kit connus (template Jardins) — réparation après sauvegarde legacy. */
const JARDINS_SECTION_TONE_BY_ID = {
  'sec-j-header': 'header',
  'sec-j-hero': 'hero-dark',
  'sec-j-saison': 'forest',
  'sec-j-quote': 'quote',
  'sec-j-galerie': 'gallery-pad',
  'sec-j-resa': 'reservation',
  'sec-j-footer': 'footer',
};

function sectionUsesJardinsKitStyle(section) {
  return (section?.components || []).some((c) =>
    String(c?.props?.kitStyle || '').startsWith('jardins'),
  );
}

const JARDINS_HERO_OVERLAY = {
  mode: 'gradient',
  angle: 90,
  stops: [
    { color: '#0d1c14', alpha: 0.78, position: 0 },
    { color: '#0d1c14', alpha: 0.56, position: 42 },
    { color: '#0d1c14', alpha: 0.12, position: 100 },
  ],
};

function repairJardinsHeroImageSection(sec) {
  if (sec.id !== 'sec-j-hero') return sec;
  const bgComp = (sec.components || []).find((c) => c.id === 'j-hero-bg');
  if (!bgComp && sec.sectionType === 'image') return sec;
  const imageUrl = bgComp?.props?.src || sec.background?.imageUrl;
  if (!imageUrl) return sec;

  const withoutBg = (sec.components || []).filter((c) => c.id !== 'j-hero-bg');

  return {
    ...sec,
    sectionType: 'image',
    components: withoutBg,
    background: {
      ...(sec.background || {}),
      type: 'image',
      enabled: true,
      imageUrl: imageUrl || sec.background?.imageUrl,
      imagePosition: sec.background?.imagePosition || 'center 53%',
      fill: sec.background?.fill ?? { mode: 'solid', color: '#334436' },
      overlayEnabled: sec.background?.overlayEnabled ?? true,
      overlay: sec.background?.overlay || JARDINS_HERO_OVERLAY,
      overlayFooter: sec.background?.overlayFooter ?? true,
    },
  };
}

const JARDINS_FOREST_MIN_ROWS = 34;

function maxComponentRowEnd(components) {
  return (components || []).reduce((max, c) => {
    const row = Number(c.row) || 0;
    const span = Number(c.rowSpan) || 1;
    return Math.max(max, row + span);
  }, 0);
}

/** Image pleine colonne gauche (section saison) — hauteur = section, pas redimensionnable à la main. */
export function isForestFillImage(comp, sec) {
  return (
    comp?.type === 'image' &&
    comp?.props?.kitStyle === 'jardins-fill' &&
    isJardinsForestSection(sec)
  );
}

/** Respecte les positions du template / éditeur — ajuste seulement hauteur section + image pleine colonne. */
export function coerceJardinsSectionGrid(sec, options = {}) {
  const syncBleedImage = options.syncBleedImage !== false;
  if (!sec || sec.designKit !== 'jardins') return sec;
  const components = [...(sec.components || [])];
  const contentEndNoFill = components.reduce((max, c) => {
    if (isForestFillImage(c, sec)) return max;
    const row = Number(c.row) || 0;
    const span = Number(c.rowSpan) || 1;
    return Math.max(max, row + span);
  }, 0);

  const requested = Number(sec.gridRows) || 0;
  let gridRows;
  if (sec.gridRowsCustom && requested > 0) {
    gridRows = Math.max(requested, contentEndNoFill);
  } else {
    gridRows = Math.max(maxComponentRowEnd(components), requested);
    if (sec.id === 'sec-j-saison' || sec.kitSectionTone === 'forest') {
      gridRows = Math.max(gridRows, JARDINS_FOREST_MIN_ROWS);
    }
  }

  let componentsChanged = false;
  let nextComponents = components;
  if (syncBleedImage) {
    nextComponents = components.map((c) => {
      if (!isForestFillImage(c, sec)) return c;
      const next = {
        ...c,
        row: 0,
        rowSpan: gridRows,
        colStart: c.colStart || 1,
        colSpan: c.colSpan || 24,
      };
      if (
        next.row !== c.row ||
        next.rowSpan !== c.rowSpan ||
        next.colStart !== c.colStart ||
        next.colSpan !== c.colSpan
      ) {
        componentsChanged = true;
        return next;
      }
      return c;
    });
  }

  if (!componentsChanged && gridRows === Number(sec.gridRows)) return sec;
  return {
    ...sec,
    gridRows,
    gridRowsCustom: true,
    components: nextComponents,
  };
}

function repairJardinsResaImageSection(sec) {
  if (sec.id !== 'sec-j-resa' && sec.kitSectionTone !== 'reservation') return sec;
  if (sec.sectionType === 'image' && sec.background?.imageUrl) return sec;
  const imageUrl =
    sec.background?.imageUrl ||
    'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1800&q=85';
  return {
    ...sec,
    sectionType: 'image',
    background: {
      ...(sec.background || {}),
      type: 'image',
      enabled: true,
      imageUrl,
      imagePosition: sec.background?.imagePosition || 'center',
      fill: sec.background?.fill ?? { mode: 'solid', color: '#203b2d' },
      overlayEnabled: true,
      overlay: sec.background?.overlay || {
        mode: 'gradient',
        angle: 90,
        stops: [
          { color: '#152b1f', alpha: 0.96, position: 0 },
          { color: '#152b1f', alpha: 0.82, position: 100 },
        ],
      },
      overlayFooter: false,
    },
  };
}

/** Restaure designKit / kitSectionTone effacés par d’anciennes sauvegardes API. */
export function repairJardinsKitSectionMeta(site) {
  if (!site?.sections?.length) return site;
  let changed = false;
  const sections = site.sections.map((sec) => {
    const jardinsLike = sec.designKit === 'jardins' || sectionUsesJardinsKitStyle(sec);
    if (!jardinsLike) return sec;
    let next = { ...sec };
    if (!next.designKit) {
      next.designKit = 'jardins';
      changed = true;
    }
    if (!next.kitSectionTone && JARDINS_SECTION_TONE_BY_ID[sec.id]) {
      next.kitSectionTone = JARDINS_SECTION_TONE_BY_ID[sec.id];
      changed = true;
    }
    const heroFixed = repairJardinsHeroImageSection(next);
    if (heroFixed !== next) {
      next = heroFixed;
      changed = true;
    }
    if (next.id === 'sec-j-hero') {
      const withoutScroll = (next.components || []).filter((c) => c.id !== 'j-hero-bot2');
      if (withoutScroll.length !== (next.components || []).length) {
        next = { ...next, components: withoutScroll };
        changed = true;
      }
    }
    if (next.id === 'sec-j-saison') {
      const comps = (next.components || []).map((c) => {
        if (c.id !== 'j-sais-title' || c.props?.kitStyle === 'jardins-seasonal-h2') return c;
        return { ...c, props: { ...c.props, kitStyle: 'jardins-seasonal-h2' } };
      });
      if (comps.some((c, i) => c !== (next.components || [])[i])) {
        next = { ...next, components: comps };
        changed = true;
      }
    }
    if (next.id === 'sec-j-resa' || next.kitSectionTone === 'reservation') {
      const resaImg = repairJardinsResaImageSection(next);
      if (resaImg !== next) {
        next = resaImg;
        changed = true;
      }
    }
    const gridCoerced = coerceJardinsSectionGrid(next, { syncBleedImage: true });
    if (gridCoerced !== next) {
      next = gridCoerced;
      changed = true;
    }
    return next;
  });
  const withSections = changed ? { ...site, sections } : site;
  return ensureJardinsResponsiveLayout(withSections);
}

/** Agrandit gridRows quand un composant est placé plus bas (toutes sections). */
export function sectionWithExpandedGridRows(section, placement, viewport = 'desktop') {
  if (!section || !placement) return section;
  const rowEnd = Number(placement.row) + (Number(placement.rowSpan) || 1);
  if (!Number.isFinite(rowEnd) || rowEnd < 1) return section;

  if (viewport === 'desktop') {
    const floor = Number(section.gridRows) || 0;
    if (rowEnd <= floor) return section;
    return { ...section, gridRows: Math.max(floor, rowEnd), gridRowsCustom: true };
  }

  const floor =
    resolveSectionConfiguredGridRows(section, viewport) ||
    Number(section[viewportSectionGridRowsField(section, viewport)]) ||
    0;
  if (rowEnd <= floor) return section;
  return applySectionGridRowsForViewport(section, viewport, Math.max(floor, rowEnd));
}

/** Le bandeau Jardins inclut déjà la nav — pas de menu page moteur en doublon. */
export function stripDuplicateJardinsPageMenu(site) {
  if (!site?.pageMenu || !siteUsesDesignKit(site, 'jardins')) return site;
  const hasHeaderBand = (site.sections || []).some(
    (sec) =>
      sec.kitSectionTone === 'header' ||
      sec.kitSectionTone === 'announcement' ||
      sec.id === 'sec-j-header' ||
      sec.id === 'sec-j-announce',
  );
  if (!hasHeaderBand) return site;
  return { ...site, pageMenu: null };
}
